import { Upload } from '@aws-sdk/lib-storage';
import { S3, GetObjectCommand, ListObjectsV2Command, DeleteObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { getAWSCredentials } from '../network/Auth';
import { getTenantRegion } from './GetTenantRegion';
import config from '../aws-exports.json';

export const getBucketName = (bucketAlias) => {
  const region = getTenantRegion();
  if (bucketAlias === 'documentsBucket') {
    return config[region].documentsBucket;
  } else if (bucketAlias === 'contentRepository') {
    return config[region].storage.bucket;
  } else {
    return bucketAlias;
  }
};

const buildClient = async (credentials) => {
  const awsCreds = credentials || (await getAWSCredentials());
  const region = getTenantRegion();
  return new S3({ credentials: awsCreds, region: region, requestChecksumCalculation: 'WHEN_REQUIRED' });
};

export const storage = buildClient();

/**
 * Uploads data to an S3 bucket using the multipart upload abstraction.
 *
 * @param {'documentsBucket' | 'contentRepository' | string} bucketAlias - The alias for the bucket (e.g., 'documentsBucket', 'contentRepository') or the direct bucket name.
 * @param {string} key - The S3 object key (path/filename) under which the data will be stored.
 * @param {Buffer|Blob|string|ReadableStream} data - The content to upload.
 * @param {import("@aws-sdk/client-s3").PutObjectCommandInput} [options] - Additional S3 upload options (e.g. ContentType, Metadata). Note: Bucket, Key, and Body are overridden by the function arguments.
 * @param {(progress: import("@aws-sdk/lib-storage").Progress) => void} [onProgress] - Optional callback function to track upload progress.
 * @returns {Promise<import("@aws-sdk/client-s3").CompleteMultipartUploadCommandOutput>} A promise that resolves with the upload result.
 */
export const uploadData = async (bucketAlias, key, data, options, onProgress) => {
  const client = await buildClient();
  const uploadParams = {
    ...options,
    Key: key,
    Bucket: getBucketName(bucketAlias),
    Body: data
  };

  const upload = new Upload({
    client: client,
    params: uploadParams,
    queueSize: 4,
    leavePartsOnError: false
  });
  if (onProgress) {
    upload.on('httpUploadProgress', (progress) => {
      onProgress(progress);
    });
  }
  return upload.done();
};

/**
 * Generates a pre-signed URL for accessing an S3 object.
 *
 * @param {'documentsBucket' | 'contentRepository' | string} bucketAlias - The alias for the bucket or the direct bucket name.
 * @param {string} key - The S3 object key.
 * @param {number} [expiresIn=3600] - Expiration time in seconds (default: 3600 = 60 minutes).
 * @returns {Promise<string>} A promise that resolves with the pre-signed URL.
 */
export const getUrl = async (bucketAlias, key, expiresIn = 3600) => {
  const client = await buildClient();
  const command = new GetObjectCommand({
    Bucket: getBucketName(bucketAlias),
    Key: key
  });
  return getSignedUrl(client, command, { expiresIn });
};

/**
 * Lists objects in an S3 bucket.
 *
 * @param {'documentsBucket' | 'contentRepository' | string} bucketAlias - The alias for the bucket or the direct bucket name.
 * @param {string} [prefix] - Limits the response to keys that begin with the specified prefix.
 * @param {import("@aws-sdk/client-s3").ListObjectsV2CommandInput} [options] - Additional options for listing (e.g., MaxKeys, ContinuationToken).
 * @returns {Promise<import("@aws-sdk/client-s3").ListObjectsV2CommandOutput>} A promise that resolves with the list of objects.
 */
export const list = async (bucketAlias, prefix, options) => {
  const client = await buildClient();
  const command = new ListObjectsV2Command({
    Bucket: getBucketName(bucketAlias),
    Prefix: prefix,
    ...options
  });
  return client.send(command);
};

/**
 * Deletes an object from an S3 bucket.
 *
 * @param {'documentsBucket' | 'contentRepository' | string} bucketAlias - The alias for the bucket or the direct bucket name.
 * @param {string} key - The S3 object key to delete.
 * @returns {Promise<import("@aws-sdk/client-s3").DeleteObjectCommandOutput>} A promise that resolves with the deletion result.
 */
export const remove = async (bucketAlias, key) => {
  const client = await buildClient();
  const command = new DeleteObjectCommand({
    Bucket: getBucketName(bucketAlias),
    Key: key
  });
  return client.send(command);
};
